/**
 * ADMISSION-001 — admission result per (student, school).
 * UI uses kebab-case in colleges_array; DB/CAM use snake_case.
 */

export const ADMISSION_RESULT_UI_KEYS = [
  'not-yet',
  'not-applying',
  'applied',
  'submitted',
  'waitlisted',
  'deferred',
  'accepted-fall',
  'accepted-spring',
  'accepted-2nd-year',
  'rejected',
  'withdrawn',
  'committed',
] as const;

export type AdmissionResultUi = (typeof ADMISSION_RESULT_UI_KEYS)[number];

export const DEFAULT_ADMISSION_RESULT: AdmissionResultUi = 'not-yet';

const DB_TO_UI: Record<string, AdmissionResultUi> = {
  not_yet: 'not-yet',
  not_applying: 'not-applying',
  applied: 'applied',
  submitted: 'submitted',
  waitlisted: 'waitlisted',
  deferred: 'deferred',
  accepted_fall: 'accepted-fall',
  accepted_spring: 'accepted-spring',
  accepted_2nd_year: 'accepted-2nd-year',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
  committed: 'committed',
};

export function normalizeAdmissionResultUi(
  value: string | null | undefined,
): AdmissionResultUi {
  if (!value) return DEFAULT_ADMISSION_RESULT;
  if ((ADMISSION_RESULT_UI_KEYS as readonly string[]).includes(value)) {
    return value as AdmissionResultUi;
  }
  if (value in DB_TO_UI) return DB_TO_UI[value];
  return DEFAULT_ADMISSION_RESULT;
}

export const ADMISSION_OPTIONS: { key: AdmissionResultUi; label: string }[] = [
  { key: 'not-yet', label: 'Not yet' },
  { key: 'not-applying', label: 'Not applying' },
  { key: 'applied', label: 'In Progress' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'waitlisted', label: 'Waitlisted' },
  { key: 'deferred', label: 'Deferred' },
  { key: 'accepted-fall', label: 'Accepted (Fall)' },
  { key: 'accepted-spring', label: 'Accepted (Spring)' },
  { key: 'accepted-2nd-year', label: 'Accepted (2nd Year)' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'withdrawn', label: 'Withdrawn' },
  { key: 'committed', label: 'Committed' },
];

/** Chip colors aligned with CAM admission-result.ts */
export const ADMISSION_CHIP_STYLE: Record<
  AdmissionResultUi,
  { background: string; color: string; border: string }
> = {
  'not-yet': { background: '#f1f5f9', color: '#334155', border: '#e2e8f0' },
  'not-applying': { background: '#e5e7eb', color: '#4b5563', border: '#d1d5db' },
  applied: { background: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
  submitted: { background: '#eef2ff', color: '#3730a3', border: '#c7d2fe' },
  waitlisted: { background: '#fffbeb', color: '#92400e', border: '#fde68a' },
  deferred: { background: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
  'accepted-fall': { background: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'accepted-spring': { background: '#ecfdf5', color: '#065f46', border: '#a7f3d0' },
  'accepted-2nd-year': { background: '#f0fdfa', color: '#115e59', border: '#99f6e4' },
  rejected: { background: '#fef2f2', color: '#991b1b', border: '#fecaca' },
  withdrawn: { background: '#f4f4f5', color: '#52525b', border: '#e4e4e7' },
  committed: { background: '#faf5ff', color: '#6b21a8', border: '#e9d5ff' },
};

export const TRACKING_ADMISSION: AdmissionResultUi[] = [
  'applied',
  'waitlisted',
  'deferred',
];

export const ACCEPTED_ADMISSION: AdmissionResultUi[] = [
  'accepted-fall',
  'accepted-spring',
  'accepted-2nd-year',
  'committed',
];

export function admissionLabelFor(key: AdmissionResultUi): string {
  return ADMISSION_OPTIONS.find((o) => o.key === key)?.label ?? 'Not yet';
}

export function admissionChipStyleFor(key: AdmissionResultUi) {
  return ADMISSION_CHIP_STYLE[key] ?? ADMISSION_CHIP_STYLE[DEFAULT_ADMISSION_RESULT];
}
